import type { QueryClient } from "@tanstack/react-query";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { qk } from "@/lib/queryKeys";

/**
 * Route-level query contracts for TanStack Router loaders and intent prefetch.
 *
 * `queryKeys.ts` remains the key-only registry, and `hooks/queries/` remains the
 * component `useQuery` layer. This file deliberately repeats the matching
 * `queryFn`s so non-React callsites can share the same `{ queryKey, queryFn }`
 * objects for `ensureQueryData` and `prefetchQuery` without depending on hook
 * lifecycles.
 */
export const chatInfiniteRouteQuery = (chatId: string) =>
  infiniteQueryOptions({
    queryKey: qk.chat(chatId),
    queryFn: ({ pageParam }: { pageParam: string | null }) => api.chat.get(chatId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.next_cursor ?? null : undefined),
  });

export const projectsListRouteQuery = () =>
  queryOptions({
    queryKey: qk.projects,
    queryFn: () => api.projects.list(),
  });

export const projectRouteQuery = (projectId: string) =>
  queryOptions({
    queryKey: qk.project(projectId),
    queryFn: () => api.projects.get(projectId),
  });

export const projectChatsRouteQuery = (projectId: string) =>
  queryOptions({
    queryKey: qk.projectChats(projectId),
    queryFn: () => api.projects.chats(projectId),
  });

export function ensureProjectRouteData(queryClient: QueryClient, projectId: string) {
  return Promise.all([
    queryClient.ensureQueryData(projectRouteQuery(projectId)),
    queryClient.ensureQueryData(projectChatsRouteQuery(projectId)),
  ]);
}

export function prefetchProjectRouteData(queryClient: QueryClient, projectId: string) {
  return Promise.all([
    queryClient.prefetchQuery(projectRouteQuery(projectId)),
    queryClient.prefetchQuery(projectChatsRouteQuery(projectId)),
  ]);
}
